import {
  ContractSwitchType,
  ApiV1Cycle,
  LineItem,
} from '@wix/ambassador-premium-store/types';
import type {
  OnInitSuccess,
  OrderPageCreationFunction,
  SubmitError,
  SummaryItem,
  SubmitPendingResponse,
  OnInitError,
} from '@wix/checkout-components';
import { action, computed, observable, runInAction } from 'mobx';
import { GTMReporters } from 'components/HOCs/withGtmLogger';
import { CycleMultiplier, ProductTypeIds } from '../../constants';
import { Experiments, PackagePickerType, PaymentCycle } from 'enums';
import Api from 'services/Api/Api';
import {
  BiEventParams,
  CheckoutPageBiEvent,
  PaymentCycle_BIMap,
} from 'services/BiLogger/BiLoggerTypes';
import { SentryLogger } from 'services/SentryLogger';
import {
  convertCycleSelectorToPaymentCycle,
  convertPaymentCycleToCycle,
} from 'services/typesUtils';
import { JsonMap } from 'types';
import { getSaleDiscountPercentage, stringifyDefinedQueryParams } from 'utils';
import { CheckoutPageProps } from './CheckoutPageProps';
import {
  DoublePurchaseResponse, // #DoublePurchase
  DoublePurchaseValidationResult, // #DoublePurchase
  DynamicDoublePurchaseValidationResult,
} from './CheckoutPageTypes';
import { convertDoublePurchaseStatusToCode } from '../../services/typesUtils';

const enum RedirectOptions {
  FROM_URL,
  SUBSCRIPTIONS_PAGE,
  SUCCESS_PAGE,
}
export class CheckoutPageController {
  @observable isInitializationFailed = false; // #NotInUse
  @observable isInitializationDone = false; // #NotInUse
  @observable isOrderPageDataLoaded = false;

  @observable orderPageUrl = ''; // #NotInUse
  @observable userEmail = ''; // #NotInUse
  @observable riskCheckIframeUrl = ''; // #NotInUse
  @observable orderPageEventAggregator = '';

  @observable currencyCode = ''; // #NotInUse

  @observable isSubmitting = false; // #NotInUse
  @observable businessError = ''; // #NotInUse
  @observable shouldShowCustomDisclaimer = false;

  // #DoublePurchase
  @observable additionalDoublePurchaseInfo: {
    info: DoublePurchaseResponse['existingPackageInfo'];
    validationCode: DoublePurchaseValidationResult;
  } = {
      info: {
        conflictingServiceGuid: '',
        planName: '',
        cycle: PaymentCycle.YEARLY,
      },
      validationCode: DoublePurchaseValidationResult.PENDING,
    };

  @observable isDoublePurchaseModalOpened = false; // #DoublePurchase
  @observable hasGoogleVoucher =
    this.props.rootStore.appStore.appContext.hasGoogleVoucher; // #NotInUse

  constructor(private readonly props: CheckoutPageProps) {
    void this.notifyGTM();
  }

  notifyGTM = async () => {
    const { storage } = this.props.rootStore.appStore;
    storage.getOrderPageViewed().then(
      () => { },
      async () => {
        await storage.setOrderPageViewed();
        this.props.reportGTM(GTMReporters.OrderPage);
      },
    );
  };

  beforeInit = async () => {
    const { fedopsLogger } = this.props.rootStore.appStore.appContext;
    fedopsLogger.beginCreateOrderPageHttpRequestInteraction();
  };

  initOrderPage: OrderPageCreationFunction = (alternativeCycle) => {
    const { setAlternativeCycle } = this.props.rootStore.appStore;
    setAlternativeCycle(
      alternativeCycle ? (alternativeCycle as ApiV1Cycle) : undefined,
    );

    const { isDynamicOffering } = this.props.rootStore.appStore.appContext;
    return isDynamicOffering
      ? this.initDynamicOrderPageData()
      : this.initClassicOrderPage();
  };

  initDynamicOrderPageData = () => {
    const { fetchOrderPageData } = this.props;
    const {
      currentProduct,
      appContext,
      currentCycle,
      alternativeCycle,
      isDowngradePlanOfDeclineChargeService,
      experiments
    } = this.props.rootStore.appStore;
    const { siteGuid, isUpgrade, currentPlanDetails, packagePickerType } = appContext;
    const isStudioSaleEnabled = experiments[Experiments.StudioSale] === 'true'
    const cartItem: LineItem = {
      cycle: alternativeCycle ?? convertPaymentCycleToCycle(currentCycle),
      quantity: 1,
      productId: currentProduct.guid || currentProduct.id.toString(),
      additionalData: {},
      // saleCoupon: isInSale ? {} : undefined, - not supported yet...
    };
    if (isStudioSaleEnabled) {
      cartItem.customOfferingFilters = { packagePickerType };
    }
    if (isUpgrade) {
      cartItem.switchContract = {
        contractSwitchType: isDowngradePlanOfDeclineChargeService
          ? ContractSwitchType.DOWNGRADE_RENEWAL
          : ContractSwitchType.CREDIT_UNUSED_PERIOD,
        existingSubscriptionId: currentPlanDetails?.userPackageGuid,
        msid: siteGuid,
      };
    } else {
      cartItem.newSubscription = { msid: siteGuid };
    }
    const cartItems = [cartItem];
    return fetchOrderPageData({ cartItems });
  };

  initClassicOrderPage = () => {
    const { fetchOrderPageData } = this.props;
    const {
      currentProduct,
      couponCode,
      appContext: { requestParamsMap, couponServiceFallback, siteGuid },
      currentCycle,
      alternativeCycle,
      isDowngradePlanOfDeclineChargeService,
    } = this.props.rootStore.appStore;
    const { oldSiteGuid, existingUserPackageGuid } = requestParamsMap;
    const orderPageData: any = {
      paymentCycle: alternativeCycle
        ? convertCycleSelectorToPaymentCycle(alternativeCycle)
        : currentCycle,
      productId: currentProduct.id,
      couponServiceFallback,
      siteGuid,
      ...(couponCode ? { couponCode } : {}),
      ...(oldSiteGuid ? { oldSiteGuid } : {}),
      ...(existingUserPackageGuid ? { existingUserPackageGuid } : {}),
      ...(isDowngradePlanOfDeclineChargeService ? { isDowngrade: true } : {}),
    };
    return fetchOrderPageData(orderPageData);
  };

  changeCycleIfRequired = (items: SummaryItem[]) => {
    try {
      const { alternativeCycle, currentCycle, setCurrentCycle } =
        this.props.rootStore.appStore;
      if (alternativeCycle && items?.length > 0) {
        const alternativePaymentCycle = convertCycleSelectorToPaymentCycle(
          items[0].cycle as ApiV1Cycle,
        );
        // cycle changed in checkout-form when user clicked on boleto "Switch to Yearly"
        currentCycle !== alternativePaymentCycle &&
          setCurrentCycle({
            cycle: alternativePaymentCycle,
            sendBiEvent: false,
          });
      }
    } catch {
      SentryLogger.logError(
        'failed in changeCycleIfRequired, items: ' + JSON.stringify(items),
      );
    }
  };

  @action.bound
  checkoutInitCompleted: OnInitSuccess = (
    orderPageEventAggregator?: string,
  ) => {
    this.isOrderPageDataLoaded = true;
    this.orderPageEventAggregator = orderPageEventAggregator ?? '';
    const { fedopsLogger } = this.props.rootStore.appStore.appContext;
    fedopsLogger.endCreateOrderPageHttpRequestInteraction();
    this.sendPageLoadedEvents();
  };

  private readonly sendPageLoadedEvents = () => {
    const { appContext, currentProduct, currentCycle } =
      this.props.rootStore.appStore;
    appContext.biLogger.logPaymentPageReadyBiEvent({
      productId: currentProduct.id,
      productGuid: currentProduct.guid,
      eventAggregator: this.orderPageEventAggregator,
      cycle: PaymentCycle_BIMap[currentCycle],
      pricing: {
        fullPrice: this.fullTermPrice,
        priceAfterDiscount: this.fullDisplayTermPrice,
      },
      currency: appContext.currencyCode,
    });
    appContext.fedopsLogger.endInitializeOrderPageInteraction();
  };

  private readonly displayInitError = (translatedMessage?: string) => {
    const { t } = this.props;
    const { updateNotification, reloadPage } =
      this.props.rootStore.notificationStore;
    updateNotification({
      isDisplayed: true,
      message: translatedMessage || t('app.notification.initialization_error'),
      actionText: t('app.notification.reload_page'),
      onActionButtonClick: reloadPage,
    });
  };

  private readonly displayPermissionsDeniedError = () => {
    const { t } = this.props;
    const { updateNotification } = this.props.rootStore.notificationStore;
    updateNotification({
      isDisplayed: true,
      message: t('app.notification.initialization_permissions_error'),
      shouldShowAction: false,
    });
  };

  private readonly sendSubmitPurchaseClickedBi = () => {
    const { biLogger } = this.props.rootStore.appStore.appContext;
    biLogger.logSubmitPurchaseClickedBiEvent(
      this.gatherCheckoutPageBiEventParams(),
    );
  };

  private readonly sendPurchaseSuccessBi = () => {
    const { biLogger } = this.props.rootStore.appStore.appContext;
    biLogger.logPurchaseSuccessBiEvent(this.gatherCheckoutPageBiEventParams());
  };

  private readonly sendPurchasePendingBi = () => {
    const { biLogger } = this.props.rootStore.appStore.appContext;
    biLogger.logPurchasePendingBiEvent(this.gatherCheckoutPageBiEventParams());
  };

  private readonly gatherCheckoutPageBiEventParams = (
    paymentMethod?: any,
    additionalInfo?: string,
  ): BiEventParams<CheckoutPageBiEvent> => {
    const { currentCycle, currentProduct } = this.props.rootStore.appStore;
    return {
      cycle: PaymentCycle_BIMap[currentCycle],
      paymentMethod,
      additionalInfo,
      eventAggregator: this.orderPageEventAggregator,
      productId: currentProduct.id,
      productGuid: currentProduct.guid,
    };
  };

  private readonly sendOrderPageErrorBi = (
    submitError: SubmitError,
    type: 'validation' | 'fatal' | 'business',
  ) => {
    const { biLogger } = this.props.rootStore.appStore.appContext;
    const biErrorReporters = {
      validation: biLogger.logOrderPageValidationErrorBiEvent,
      fatal: biLogger.logOrderPageFatalErrorBiEvent,
      business: biLogger.logOrderPageBusinessErrorBiEvent,
    };
    const additionalInfo =
      submitError.error.message || submitError.failureReason;
    biErrorReporters[type](
      this.gatherCheckoutPageBiEventParams('', additionalInfo),
    );
  };

  // #DoublePurchase
  private readonly sendDoublePurchaseBi = () => {
    const { guid } = this.props.rootStore.appStore.currentProduct;
    const conflictingServiceGuid =
      this.additionalDoublePurchaseInfo.info?.conflictingServiceGuid ?? '';
    const reason =
      this.additionalDoublePurchaseInfo.validationCode ===
        DoublePurchaseValidationResult.ALREADY_DELIVERED
        ? 'already-delivered'
        : 'pending';
    this.props.rootStore.appStore.appContext.biLogger.logDoublePurchasePopupViewed(
      {
        productGuid: guid,
        productTypeId: ProductTypeIds.PremiumPlans,
        conflictingServiceGuid,
        reason,
      },
    );
  };

  // #SuccessPage
  private readonly getSuccessUrl = (): string => {
    return (
      this.props.rootStore.appStore.appContext.requestParamsMap.successUrl || ''
    );
  };

  // #SuccessPage
  private readonly redirectToSecondUpgradeSuccessPage = (
    invoiceId: string,
    domainName?: string | null,
  ) => {
    const url = this.getPremiumSuccessPagesUrl({
      invoiceId,
      domainName,
      secondUpgradeSuccess: true,
    });
    window.location.assign(url);
  };

  private readonly getSiteDomainName: () => Promise<null | string> = (() => {
    let domainName = null; // will be cached after first call
    const getDomainFunc = async () => {
      if (domainName) {
        return domainName;
      }
      const { siteGuid } = this.props.rootStore.appStore.appContext;
      try {
        const res = await Api.getAssignedDomains(siteGuid);
        domainName = res?.data?.sites[0]?.primary?.domainName;
        return domainName;
      } catch {
        return null;
      }
    };
    return getDomainFunc;
  })();

  // #SuccessPage
  private async shoudRedirectToSecondUpgradeSuccess() {
    const { experiments } = this.props;
    const { isUpgrade } = this.props.rootStore.appStore.appContext;
    if (
      experiments.enabled(Experiments.SecondUpgradeNewSuccessPage) &&
      isUpgrade
    ) {
      const siteDomainName = await this.getSiteDomainName();
      return !!siteDomainName;
    }
    return false;
  }

  // #SuccessPage
  private readonly shouldRedirectToSuccessFromUrl = () => {
    return this.getSuccessUrl() && this.getSuccessUrl().length > 0;
  };

  // #SuccessPage
  private geteDomainHelperSuccessUrl(url: string, invoiceId: string) {
    const { currentCycle, currentProduct } = this.props.rootStore.appStore;
    const params = {
      invoiceId,
      planName: currentProduct.productCode,
      cycle: currentCycle,
      paymentType: 'IMMIDIATE',
    };
    const urlParams = Object.keys(params)
      .map((paramKey) => paramKey + '=' + encodeURIComponent(params[paramKey]))
      .join('&');
    const concatinationSymbol = this.isUrlContainsQueryParams(url) ? '&' : '?';
    return url + concatinationSymbol + urlParams;
  }

  private isUrlContainsQueryParams(url: string): boolean {
    return new URL(url).search.length > 0;
  }

  // #DoublePurchase
  @action.bound
  handleDoublePurchase() {
    const { fedopsLogger } = this.props.rootStore.appStore.appContext;
    this.sendDoublePurchaseBi();
    fedopsLogger.endSubmitOrderPageInteraction();
    this.isDoublePurchaseModalOpened = true;
  }

  // #DoublePurchase
  @action.bound
  closeDoublePurchaseModal() {
    this.isDoublePurchaseModalOpened = false;
  }

  @action.bound
  setShouldShowCustomDisclaimer(val) {
    this.shouldShowCustomDisclaimer = val;
  }

  handleInitError: OnInitError = (
    error,
    orderPageEventAggregator,
    errorInfo,
  ) => {
    const {
      removeUnloadListener,
      appContext: { fedopsLogger },
    } = this.props.rootStore.appStore;
    const errorCode = (error as any)?.response?.data?.details?.applicationError
      ?.code;
    removeUnloadListener && removeUnloadListener();
    const isPermissionsDeniedError =
      errorCode && Api.PERMISSION_DENIED_ERROR_CODES.includes(errorCode);
    const isPermissionDeniedErrorEnabled = this.props.experiments.enabled(
      Experiments.PermissionDeniedError,
    );
    if (isPermissionDeniedErrorEnabled && isPermissionsDeniedError) {
      this.displayPermissionsDeniedError();
    } else {
      this.displayInitError(errorInfo?.translatedErrorMessage);
    }

    if (orderPageEventAggregator) {
      this.orderPageEventAggregator = orderPageEventAggregator;
    }
    if (errorCode && Api.BUSINESS_ERROR_CODES.indexOf(errorCode) !== -1) {
      fedopsLogger.endCreateOrderPageHttpRequestInteraction();
    }
    SentryLogger.logError(error.data ? error.data.errorDescription : error);
  };

  // #UrlHandling
  private readonly redirectToSepaPendingPage = (invoiceId: string): void => {
    const isRealativeSepaSuccessPageRedirectExpEnabled =
      this.props.experiments.enabled(Experiments.RelativeSepaSuccessRedirect);
    const {
      appContext: { siteGuid },
    } = this.props.rootStore.appStore;

    const queryParams = new URLSearchParams('referrer=PremiumPurchasePlan');
    if (siteGuid) {
      queryParams.append('siteId', siteGuid);
    }

    const sepaPendingPage = isRealativeSepaSuccessPageRedirectExpEnabled
      ? `/premium-success-pages/${invoiceId}?${queryParams.toString()}`
      : `https://premium.wix.com/wix/api/SEPAPaymentPending?v=1&invoiceId=${invoiceId}&referralAdditionalInfo=premium-purchase-plan`;

    window.location.assign(sepaPendingPage);
  };

  // #UrlHandling
  private readonly redirectResolver = (): RedirectOptions => {
    if (this.shouldRedirectToSubscriptionsPage()) {
      return RedirectOptions.SUBSCRIPTIONS_PAGE;
    }
    if (this.shouldRedirectToSuccessFromUrl()) {
      return RedirectOptions.FROM_URL;
    }
    return RedirectOptions.SUCCESS_PAGE;
  };

  // #SuccessPage
  private readonly redirectToBoletoSuccessPage = (invoiceId: string): void => {
    const boletoSuccessPageUrl = this.getPremiumSuccessPagesUrl({ invoiceId });
    window.location.assign(boletoSuccessPageUrl);
  };

  // #UrlHandling
  private readonly redirectToSiteLevelSubscritionPage = (
    baseUrl: string,
    siteGuid: string,
  ): void => {
    window.location.assign(
      `${baseUrl}dashboard/${siteGuid}/settings/subscriptions?referralInfo=AdvancedPlans_downgradePlan`,
    );
  };

  // #SuccessPage
  private readonly getPremiumSuccessPagesUrl = ({
    invoiceId,
    secondUpgradeSuccess,
    domainName,
  }: {
    invoiceId: string;
    secondUpgradeSuccess?: boolean;
    domainName?: string | null;
  }) => {
    const { appContext, baseUrl } = this.props.rootStore.appStore;
    const { siteGuid, eventAggregator, packagePickerType } = appContext;

    const params = {
      referrer: 'PremiumPurchasePlan',
      siteId: siteGuid,
      ppType:
        packagePickerType === PackagePickerType.Studio ||
          packagePickerType === PackagePickerType.EditorXToStudio
          ? PackagePickerType.Studio
          : '',
      domainName: domainName ?? '',
      externalSessionId: eventAggregator,
      secondUpgrade: secondUpgradeSuccess
        ? secondUpgradeSuccess?.toString()
        : '',
    };
    const queryParamsStr = stringifyDefinedQueryParams(params);
    return `${baseUrl}premium-success-pages/${invoiceId}${queryParamsStr}`;
  };

  // #UrlHandling
  private readonly shouldRedirectToSubscriptionsPage = () =>
    Boolean(
      this.props.rootStore.appStore.appContext.requestParamsMap.oldSiteGuid,
    );

  // #SuccessPage
  private readonly redirectToSuccessPage = (invoiceId: string) => {
    const redirectionResult = this.redirectResolver();
    switch (redirectionResult) {
      case RedirectOptions.SUCCESS_PAGE:
        return this.redirectToPlanSuccessPage(invoiceId);
      case RedirectOptions.FROM_URL:
        return this.redirectToSuccessFromUrl(invoiceId);
      case RedirectOptions.SUBSCRIPTIONS_PAGE:
        return this.redirectToSubscriptionsPage();
      default:
        return this.redirectToPlanSuccessPage(invoiceId);
    }
  };

  // #SuccessPage
  private readonly redirectToDayfulSuccessPage = (invoiceId: string) => {
    const {
      baseUrl,
      appContext: { siteGuid },
    } = this.props.rootStore.appStore;

    const queryParams = {
      siteId: siteGuid,
      referrer: 'PurchaseDayful',
    };
    this.navigateToSuccess(
      `${baseUrl}premium-success-pages/dayful/${invoiceId}`,
      queryParams,
    );
  };

  // #UrlHandling
  private readonly redirectToSubscriptionsPage = () =>
    window.location.assign(
      this.generateUrlWithQueryParams('/wix/api/PaymentConsole', {
        referralAdditionalInfo: 'package-picker',
        showAssignToast: true,
        targetSite: this.props.rootStore.appStore.appContext.siteName,
      }),
    );

  private sendSubmitCheckoutReassignDomainFlowBi = ({
    domainName,
    referralAdditionalInfo,
  }: {
    domainName?: string;
    referralAdditionalInfo: string;
  }) => {
    const { biLogger, siteGuid, userGuid, referral } =
      this.props.rootStore.appStore.appContext;
    biLogger.logSubmitCheckoutReassignDomainFlow({
      msid: siteGuid,
      uuid: userGuid,
      domain: domainName,
      referralAdditionalInfo: referralAdditionalInfo ?? referral,
    });
  };

  // #SuccessPage
  private async redirectToPlanSuccessPage(invoiceId: string) {
    const { baseUrl } = this.props.rootStore.appStore;
    const referralAdditionalInfo =
      new URLSearchParams(window.location.search).get(
        'referralAdditionalInfo',
      ) ?? 'PremiumPurchasePlan';
    const domainName = new URLSearchParams(window?.location?.search).get(
      'domainName',
    );
    const isConnectIntent =
      referralAdditionalInfo === 'add-domain-connect-intent';
    const {
      appContext: {
        siteGuid,
        siteName,
        replaceDomainVoucherWithPlanDiscount,
        isUpgrade,
      },
    } = this.props.rootStore.appStore;

    const queryParams = {
      metaSiteId: siteGuid,
      siteName,
      isAnonymousDomain: !!replaceDomainVoucherWithPlanDiscount,
      invoiceId,
      isUpgrade,
      referrer: referralAdditionalInfo,
      ...(isConnectIntent ? { reassignStatus: domainName } : {}),
    };
    const isReassignSuccessPageEnabled = this.props.experiments.enabled(
      Experiments.ReassignSuccessPage,
    );
    // TODO: Move getIsRepurchase API conditioning into premium-success-pages when PP defaultly redirects traffic to it
    const isReassignIntent =
      !isUpgrade && (await Api.getIsRepurchase(queryParams));

    const shouldRedirectToReassignSuccess =
      isReassignSuccessPageEnabled &&
      ((!isUpgrade && isConnectIntent) || isReassignIntent);

    const reassignDomain =
      typeof queryParams.reassignStatus === 'string'
        ? queryParams.reassignStatus
        : undefined;

    isReassignIntent &&
      this.sendSubmitCheckoutReassignDomainFlowBi({
        domainName: domainName ?? reassignDomain,
        referralAdditionalInfo,
      });

    if (await this.shoudRedirectToSecondUpgradeSuccess()) {
      const siteDomainName = await this.getSiteDomainName();
      this.redirectToSecondUpgradeSuccessPage(invoiceId, siteDomainName);
    } else if (shouldRedirectToReassignSuccess) {
      this.navigateToSuccess(
        `${baseUrl}premium-success-pages/reassign/${invoiceId}`,
        queryParams,
      );
    } else {
      if (
        this.props.experiments.enabled(
          Experiments.EnableNewPlanSuccesPageDesktop,
        )
      ) {
        const domainNameAsQueryParam = domainName
          ? `&domainName=${domainName}`
          : '';
        return this.navigateToSuccess(
          `${baseUrl}premium-success-pages/premium-plan-success/` +
          `${invoiceId}?siteId=${siteGuid}` +
          `&referrer=PremiumPurchasePlan` +
          `&referralAdditionalInfo=${referralAdditionalInfo}` +
          `${domainNameAsQueryParam}`,
          {},
        );
      }
      this.navigateToSuccess(`${baseUrl}store/plansSuccessPage`, {
        invoiceId,
        referralAdditionalInfo,
        newPurchase: !isUpgrade,
      });
    }
  }

  // #SuccessPage
  private readonly navigateToSuccess = (url: string, queryParams: JsonMap) => {
    const urlWithParams = this.generateUrlWithQueryParams(url, queryParams);
    window.location.assign(urlWithParams);
  };

  // #UrlHandling
  private readonly generateUrlWithQueryParams = (
    url: string,
    queryParams: JsonMap,
  ) =>
    encodeURI(
      Object.entries(queryParams).reduce(
        (acc, [paramName, paramValue], index) =>
          acc + `${index ? '&' : '?'}${paramName}=${paramValue}`,
        url,
      ),
    );

  // #SuccessPage
  private redirectToSuccessFromUrl(invoiceId: string) {
    window.location.assign(
      this.geteDomainHelperSuccessUrl(this.getSuccessUrl(), invoiceId),
    );
  }

  paymentSuccessful = (args: { invoiceId: string; invoiceStatus: string }) => {
    const { invoiceId, invoiceStatus } = args;

    const {
      appContext,
      removeUnloadListener,
      isDowngradePlanOfDeclineChargeService,
      baseUrl,
      isDayful,
    } = this.props.rootStore.appStore;
    const { siteGuid } = appContext;
    removeUnloadListener && removeUnloadListener();
    const { fedopsLogger } = appContext;
    fedopsLogger.endSubmitOrderPageInteraction();
    this.props.reportGTM(GTMReporters.PurchaseSuccess);
    if (isDayful) {
      this.redirectToDayfulSuccessPage(invoiceId);
    } else if (isDowngradePlanOfDeclineChargeService) {
      this.redirectToSiteLevelSubscritionPage(baseUrl, siteGuid);
    } else if (invoiceStatus === 'PAYMENT_IN_PROGRESS') {
      this.sendPurchasePendingBi();
      this.redirectToSepaPendingPage(invoiceId);
    } else if (invoiceStatus === 'DRAFT') {
      this.redirectToBoletoSuccessPage(invoiceId);
    } else if (invoiceId) {
      this.sendPurchaseSuccessBi();
      this.redirectToSuccessPage(invoiceId);
    }
  };

  onSubmitPending = (): SubmitPendingResponse => {
    const { appContext, removeUnloadListener } = this.props.rootStore.appStore;
    removeUnloadListener && removeUnloadListener();

    const { fedopsLogger } = appContext;
    fedopsLogger.endSubmitOrderPageInteraction();

    return {
      preventRedirect: false,
    };
  };

  @action.bound
  handlePurchaseError(submitError: SubmitError) {
    const { appContext, removeUnloadListener } = this.props.rootStore.appStore;
    const { fedopsLogger } = appContext;
    removeUnloadListener && removeUnloadListener();
    switch (submitError.failureReason) {
      case 'SUBMIT_BUSINESS_ERROR':
        this.sendOrderPageErrorBi(submitError, 'business');
        fedopsLogger.endSubmitOrderPageInteraction();
        break;
      case 'SUBMIT_VALIDATION_ERROR':
        this.sendOrderPageErrorBi(submitError, 'validation');
        break;
      case 'SUBMIT_FATAL_ERROR':
        this.sendOrderPageErrorBi(submitError, 'fatal');
        fedopsLogger.endSubmitOrderPageInteraction();
        SentryLogger.logError(submitError.error);
        break;
      default:
        SentryLogger.logError(submitError.error || submitError.failureReason);
    }
  }

  beforeSubmit = () => {
    const { appContext } = this.props.rootStore.appStore;
    const { fedopsLogger } = appContext;

    this.sendSubmitPurchaseClickedBi();
    fedopsLogger.beginSubmitOrderPageInteraction();
    return this.isDoublePurchase().then((result) => {
      if (!result) {
        return true;
      }
      this.handleDoublePurchase();
      return false;
    });
  };

  // #DoublePurchase
  @action.bound
  async isDoublePurchase(): Promise<boolean> {
    const { isDynamicOffering } = this.props.rootStore.appStore.appContext;
    return isDynamicOffering
      ? this.isDynamicDoublePurchase()
      : this.isClassicDoublePurchase();
  }

  // #DoublePurchase
  private readonly isDynamicDoublePurchase = async () => {
    try {
      const { siteGuid, isUpgrade } = this.props.rootStore.appStore.appContext;
      if (isUpgrade) {
        return false;
      }
      const { status } = await Api.verifyDynmaicDoublePurchase({
        siteGuid,
      });
      const isDoublePurchase =
        status === DynamicDoublePurchaseValidationResult.DELIVERED ||
        status === DynamicDoublePurchaseValidationResult.PENDING;
      if (isDoublePurchase) {
        const validationCode = convertDoublePurchaseStatusToCode(status);
        this.additionalDoublePurchaseInfo.validationCode = validationCode;
      }
      return isDoublePurchase;
    } catch (e) {
      SentryLogger.logError(e);
    }
    return false;
  };

  // #DoublePurchase
  private readonly isClassicDoublePurchase = async () => {
    try {
      const {
        currentCycle: paymentCycle,
        currentProduct,
        appContext: { isUpgrade, siteGuid },
      } = this.props.rootStore.appStore;
      const {
        success,
        existingPackageInfo,
        validationCode: code,
      } = await Api.verifyDoublePurchase({
        upgrade: isUpgrade,
        appDefId: 0,
        paymentCycle,
        productId: currentProduct.id,
        eventAggregator: this.orderPageEventAggregator,
        siteGuid,
      });
      const isDoublePurchase = !(
        success ||
        !existingPackageInfo ||
        code === DoublePurchaseValidationResult.GENERAL_ERROR
      );
      if (isDoublePurchase) {
        runInAction(() => {
          this.additionalDoublePurchaseInfo = {
            info: existingPackageInfo,
            validationCode: code,
          };
        });
      }
      return isDoublePurchase;
    } catch (error) {
      SentryLogger.logError(error);
    }
    return false;
  };

  @computed
  get currentCycleMultiplier(): number {
    return CycleMultiplier[this.props.rootStore.appStore.currentCycle];
  }

  @computed
  get currentProductCycle() {
    const { currentCycle, currentProduct } = this.props.rootStore.appStore;
    return (
      currentProduct.cycles.find((c) => c.cycleId === currentCycle) ||
      currentProduct.cycles[0]
    );
  }

  @computed
  get fullTermPrice() {
    return (
      this.currentProductCycle.fullTermDisplayPrice +
      this.currentProductCycle.fullTermDisplaySavings
    );
  }

  get fullDisplayTermPrice() {
    return this.currentProductCycle.fullTermDisplayPrice;
  }

  @computed
  get fullTermDisplaySavings(): number {
    return this.currentProductCycle.fullTermDisplaySavings;
  }

  get monthlyFullPrice() {
    const { currentProduct } = this.props.rootStore.appStore;
    const monthlyCycle = currentProduct.cycles.find(
      (c) => c.cycleId === PaymentCycle.MONTHLY,
    );
    return monthlyCycle?.monthlyFullPrice;
  }

  get showStrikedPrice() {
    const { experiments } = this.props;

    return (
      this.showDiscountSavings &&
      !experiments.enabled(Experiments.RemoveStrikedPrice)
    );
  }

  get showDiscountSavings() {
    const { experiments } = this.props;
    const { currentCycle, isInSale } = this.props.rootStore.appStore;

    return (
      !isInSale &&
      currentCycle !== PaymentCycle.MONTHLY &&
      experiments.enabled(Experiments.CheckoutSavings)
    );
  }

  getSummeryPrices = (): number[] => {
    const { experiments } = this.props;
    const { fullTermDisplayPrice, monthlyFullPrice, fullTermPriceBeforeTax } =
      this.currentProductCycle;
    if (
      experiments.enabled(Experiments.NoVatInCheckoutSummery) &&
      fullTermPriceBeforeTax
    ) {
      const monthlyPriceBefore =
        Math.ceil(
          (fullTermPriceBeforeTax * 100) / this.currentCycleMultiplier,
        ) / 100;
      return [monthlyPriceBefore, fullTermPriceBeforeTax];
    }
    return [monthlyFullPrice, fullTermDisplayPrice];
  };

  getCouponLabel = (couponName) => {
    const { t } = this.props;
    const { isInSale, isResellerOffering } = this.props.rootStore.appStore;
    const { saleParams: { discountPercent } = {} } = this.props.rootStore.appStore.appContext;

    const couponNamesMap = {
      partner_discount: 'checkout.partner_copoun',
      domain_discount: 'checkout.domain_coupon',
      Wix2XMigration40: 'checkout_special_discount_40',
    };
    if (couponNamesMap[couponName]) {
      return t(couponNamesMap[couponName]);
    }
    if (isInSale) {
      const percent = isResellerOffering ? discountPercent : getSaleDiscountPercentage();
      return t('sale.product.x_off', { percent });
    }
    return '';
  };
}
